import Appearance from "../shapes/Appearance";
import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { DEFAULT_FILL_COLOR, DEFAULT_STROKE_COLOR } from "../shared/constants";
import ModeSelector from "../sidebar/edit/Fields/ModeSelector";

export default class DrawMenu extends AbstractProvidedClass {
    appearance = null;
    mode = null;

    constructor(provider) {
        super(provider);

        this.appearance = new Appearance(this.provider, {
            fillColor: this.p.color(DEFAULT_FILL_COLOR),
            strokeColor: this.p.color(DEFAULT_STROKE_COLOR),
            strokeWidth: null,
        });

        this.container = this.p.createDiv()
            .parent('#p5canvas')
            .class('planner-toolbar-vert');

        this.createColorTools();
        this.createModeSelector();
        this.createOrderButtons();

        this.hide();
    }

    createColorTools() {
        let fillRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        let fillH4 = this.p.createElement('h4', 'Fill')
            .parent(fillRow)
            .class('row input-label');
        this.fillPicker = this.p.createColorPicker(DEFAULT_FILL_COLOR)
            .parent(fillRow)
            .class('input-color')
            .input(() => this.changeFillColor());
        this.useFill = this.p.createCheckbox(null, true)
            .parent(fillRow)
            .changed(() => this.changeUseFill());
        this.useFill.elt.firstChild.firstChild.id = 'use-fill';

        let strokeRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        let strokeH4 = this.p.createElement('h4', 'Stroke')
            .parent(strokeRow)
            .class('row input-label');
        this.strokePicker = this.p.createColorPicker(DEFAULT_STROKE_COLOR)
            .parent(strokeRow)
            .class('input-color')
            .input(() => this.changeStrokeColor());
        this.useStroke = this.p.createCheckbox(null, true)
            .parent(strokeRow)
            .changed(() => this.changeUseStroke());
        this.useStroke.elt.firstChild.firstChild.id = 'use-stroke';
    }

    createModeSelector() {
        this.modeSelector = ModeSelector.setup(this.provider)
            .parent(this.container)
            .make()
            .setDrawMode('rectangle');
    }

    createOrderButtons() {
        this.orderButtons = this.p.createDiv()
            .parent(this.container);
        let orderLabel = this.p.createElement('h4', 'Position')
            .class('input-label')
            .parent(this.orderButtons);

        this.buttonContainer = this.p.createDiv()
            .parent(this.orderButtons)
            .class('row row-between');

        this.createOrderButton(
            'Bring to front',
            () => this.elements.bringToFront(this.selection.first),
            'bring-to-front'
        );
        this.createOrderButton(
            'Send to back',
            () => this.elements.sendToBack(this.selection.first),
            'send-to-back'
        );
        this.createOrderButton(
            'Bring forward',
            () => this.elements.bringForward(this.selection.first),
            'bring-forward'
        );
        this.createOrderButton(
            'Send backward',
            () => this.elements.sendBackward(this.selection.first),
            'send-backward'
        );
    }

    createOrderButton(tooltip, func, svgId) {
        let btn = this.p.createDiv()
            .parent(this.buttonContainer)
            .attribute('data-tooltip', tooltip)
            .mouseClicked(func)
            .class('btn-icon-clear');
        let svg = SVG().addTo(btn.elt);
        svg.use(svgId, '/icons/__remixicon.sprite.svg');
        btn.elt.children[0].classList.add('icon');
        // console.log(":: ", btn.elt.children[0])

        return btn;
    }

    changeUseFill() {
        if (this.useFill.checked()) {
            this.fillPicker.removeAttribute('disabled');
            this.appearance.fillColor = this.fillColor;
        } else {
            this.fillPicker.attribute('disabled', true);
            this.appearance.fillColor = null;
        }
        this.checkChangesMade();
    }

    changeUseStroke() {
        if (this.useStroke.checked()) {
            this.strokePicker.removeAttribute('disabled');
            this.appearance.strokeColor = this.strokeColor;
        } else {
            this.strokePicker.attribute('disabled', true);
            this.appearance.strokeColor = null;
        }
        this.checkChangesMade();
    }

    changeFillColor() {
        this.appearance.fillColor = this.fillColor;
        this.checkChangesMade();
    }

    changeStrokeColor() {
        this.appearance.strokeColor = this.strokeColor;
        this.checkChangesMade();
    }

    setAppearance(appearance) {
        this.appearance = appearance;
        if (this.appearance.fillColor) {
            this.useFill.checked(true);
            this.fillPicker.value(this.appearance.fillColor.toString('#rrggbb'));
            this.fillPicker.removeAttribute('disabled');
        } else {
            this.useFill.checked(false);
            this.fillPicker.attribute('disabled', true);
        }

        if (this.appearance.strokeColor) {
            this.useStroke.checked(true);
            this.strokePicker.value(this.appearance.strokeColor.toString('#rrggbb'));
            this.strokePicker.removeAttribute('disabled');
        } else {
            this.useStroke.checked(false);
            this.strokePicker.attribute('disabled', true);
        }
    }

    show(mode) {
        this.container.show();
        this.mode = mode;
        if (mode == 'draw') {
            this.modeSelector.show();
            this.orderButtons.hide();
        } else {
            this.modeSelector.hide();
            this.orderButtons.show();
        }
    }

    hide() {
        this.appearance = this.appearance.clone();
        this.container.hide();
    }

    checkChangesMade() {
        if (this.mode == 'element') {
            this.selection.first.row.updateImg();
            this.changesMade();
        }
    }

    get fillColor() {
        return this.p.color(this.fillPicker.color());
    }

    get strokeColor() {
        return this.p.color(this.strokePicker.color());
    }

    get stroke() {
        return this.useStroke.checked() == 1;
    }

    set stroke(stroke) {
        this.useStroke.checked(stroke ? 1 : 0);
        this.changeUseStroke();
    }

    get fill() {
        return this.useFill.checked() == 1;
    }

    set fill(fill) {
        this.useFill.checked(fill ? 1 : 0);
        this.changeUseFill();
    }
}
